import type { TFunction } from 'i18next';

import type { HooterStatus, IssueReading, ReadingTrend, StartedBy } from '../../types/domain.types';
import { formatReading } from '../../utils/format';

export interface AlertRecordFields {
  readonly title?: string;
  readonly issueTitle: string | null;
  readonly plantName: string | null;
  readonly location: string | null;
  readonly reading: IssueReading | null;
  readonly startedBy: StartedBy | null;
  readonly hooter?: HooterStatus | null;
}

export interface AlertLines {
  readonly title: string;
  readonly reading: string | null;
  readonly readingContext: string | null;
  readonly claim: string;
  readonly rowClaim: string;
  readonly meta: string;
  readonly placeMeta: string;
  readonly hooter: string | null;
}

const SEPARATOR = ' · ';

const joinParts = (parts: readonly (string | null)[]): string =>
  parts.filter((part): part is string => part !== null && part !== '').join(SEPARATOR);

const trendWord = (t: TFunction, trend: ReadingTrend): string => {
  switch (trend) {
    case 'RISING':
      return t('closeTheLoop.reading.rising', 'rising');
    case 'FALLING':
      return t('closeTheLoop.reading.falling', 'falling');
    case 'STEADY':
      return t('closeTheLoop.reading.steady', 'steady');
  }
};

export const readingValue = (reading: IssueReading): string => `${formatReading(reading.value)} ${reading.unit}`.trim();

export const readingContext = (t: TFunction, reading: IssueReading): string | null => {
  const parts = [reading.band, reading.trend === null ? null : trendWord(t, reading.trend)].filter(
    (part): part is string => part !== null && part !== '',
  );
  return parts.length === 0 ? null : parts.join(', ');
};

const claimText = (t: TFunction, startedBy: StartedBy | null): string =>
  startedBy === null
    ? t('closeTheLoop.alert.nobodyStarted', 'nobody has started')
    : t('closeTheLoop.alert.startedBy', '{{name}} started', { name: startedBy.name });

const rowClaimText = (t: TFunction, startedBy: StartedBy | null): string =>
  startedBy === null ? t('closeTheLoop.alert.unclaimed', 'unclaimed') : claimText(t, startedBy);

const NAME_JOIN = ', ';
const LIST_OPTIONS: Intl.ListFormatOptions = { style: 'short', type: 'unit' };
const LISTS = new Map<string, Intl.ListFormat | null>();

const hasListFormat = (): boolean => typeof Intl !== 'undefined' && typeof Intl.ListFormat === 'function';

const listFormatFor = (locale: string): Intl.ListFormat | null => {
  if (!hasListFormat()) return null;
  try {
    return new Intl.ListFormat(locale, LIST_OPTIONS);
  } catch {
    return null;
  }
};

const namesList = (names: readonly string[], locale: string): string => {
  if (!LISTS.has(locale)) LISTS.set(locale, listFormatFor(locale));
  const list = LISTS.get(locale) ?? null;
  return list === null ? names.join(NAME_JOIN) : list.format(names);
};

export const hooterLine = (t: TFunction, hooter: HooterStatus | null, locale: string): string | null => {
  if (hooter === null) return null;
  switch (hooter.state) {
    case 'SOUNDING':
      return hooter.calling.length === 0
        ? t('closeTheLoop.alert.hooterSounding', 'Hooter sounding')
        : t('closeTheLoop.alert.hooterCalling', 'Hooter sounding · calling {{names}}', {
            names: namesList(hooter.calling, locale),
          });
    case 'SILENCED':
      return t('closeTheLoop.alert.hooterSilenced', 'Hooter silenced');
    case 'UNACKNOWLEDGED':
      return t('closeTheLoop.alert.hooterUnconfirmed', 'Hooter not confirmed — check on site');
    case 'OFF':
      return null;
  }
};

export const emergencyEyebrow = (t: TFunction, plantName: string | null): string =>
  plantName === null
    ? t('closeTheLoop.inApp.emergency', 'Emergency')
    : t('closeTheLoop.inApp.emergencyAt', 'Emergency · {{plant}}', { plant: plantName });

export const firedLine = (t: TFunction, location: string | null, when: string): string => {
  const fired = t('closeTheLoop.alert.fired', 'fired {{when}}', { when });
  return location === null ? fired : joinParts([location, fired]);
};

export const alertLines = (t: TFunction, record: AlertRecordFields, locale: string): AlertLines => {
  const reading = record.reading === null ? null : readingValue(record.reading);
  const claim = claimText(t, record.startedBy);
  return {
    title: record.issueTitle ?? record.title ?? '',
    reading,
    readingContext: record.reading === null ? null : readingContext(t, record.reading),
    claim,
    rowClaim: rowClaimText(t, record.startedBy),
    meta: joinParts([record.location, reading, claim]),
    placeMeta: joinParts([record.plantName, record.location, reading, claim]),
    hooter: hooterLine(t, record.hooter ?? null, locale),
  };
};
